import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { MeetingNotesOut, SessionResultOut } from "../contract/api";
import { captureAnalytics } from "../analytics";
import { t } from "../contract/strings";
import {
  CloseIcon,
  DownloadIcon,
  SearchIcon,
  Timeline,
  cx,
  fmtClock,
  speakerName,
  timelineEntries,
  type TimelineEntry,
} from "../ui";
import { meetingTitle } from "./meeting-title";
import { fmtDate, fmtDuration } from "./past-sessions";
import {
  HIT_44,
  HIT_CHIP,
  LABEL,
  filterSegments,
  highlightParts,
  resultCommitments,
  transcriptSpeakers,
  turnAt,
} from "./result-model";
import { notesExportLines } from "./result-notes-model";

export const TRANSCRIPT_PAGE_SIZE = 10;

/** How long the turn a jump landed on stays tinted. */
export const FOCUS_MS = 2500;

/** A request from another tab to show one moment: `nonce` makes a repeat jump a new one. */
export interface JumpRequest {
  time: number;
  nonce: number;
}

/** Room left between the sticky header's bottom and a jumped-to turn. */
const JUMP_GAP_PX = 16;

/**
 * Where to scroll the window so `row` sits just below the sticky details card and tab bar
 * once the card has folded. A jump folds the card in the same render, but the fold
 * animates, so the layout read here may still be the open card's: the fold row's current
 * height is taken off both the turn's place (the content above it shrinks by that much)
 * and the sticky header's height (it ends up that much shorter).
 */
export function jumpScrollTop(row: Element): number {
  const sticky = document.querySelector("[data-result-sticky]");
  const foldNow = sticky?.querySelector("[data-details-fold]")?.getBoundingClientRect().height ?? 0;
  const stickyFolded = (sticky?.getBoundingClientRect().height ?? 0) - foldNow;
  const rowTopFolded = row.getBoundingClientRect().top + window.scrollY - foldNow;
  return Math.max(0, Math.round(rowTopFolded - stickyFolded - JUMP_GAP_PX));
}

/**
 * Download the notes and the whole transcript as one text file: the summary, the next
 * steps, the notes written in each style (#774), then every turn.
 */
export function exportTranscript(result: SessionResultOut, notes: MeetingNotesOut | null = null): void {
  const lines: string[] = [];
  const dateStr = result.created_at ? fmtDate(result.created_at) : null;
  lines.push(`# ${meetingTitle(result.title, result.created_at)}`);
  if (dateStr) lines.push(t("result.export_date", { date: dateStr }));
  lines.push(t("result.export_duration", { duration: fmtDuration(result.duration_sec) }));
  if (result.state === "partial") lines.push(t("result.partial_note"));
  lines.push("");

  if (result.summary) {
    lines.push(t("result.export_summary_heading"));
    lines.push(result.summary);
    lines.push("");
  }

  const commitments = resultCommitments(result);
  if (commitments.length) {
    lines.push(t("result.export_next_steps_heading"));
    for (const item of commitments) {
      lines.push(
        `- [${speakerName(item.speaker)}] ${item.text}${item.due ? t("result.export_due", { due: item.due }) : ""}`,
      );
    }
    lines.push("");
  }

  lines.push(...notesExportLines(notes));

  lines.push(t("result.export_transcript_heading"));
  for (const seg of result.segments) {
    lines.push(
      `[${fmtClock(seg.start)} - ${fmtClock(seg.end)}] ${speakerName(seg.speaker)}: ${seg.text || ""}`,
    );
  }

  const shortId = result.session_id.slice(-8).toLowerCase();
  const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `session-${shortId}-transcript.txt`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const CHIP_BUTTON =
  // Names in their own case (#766): no caps, so no caps tracking either.
  "font-code text-count font-semibold border rounded-pill px-chip-x py-1 cursor-pointer transition-colors";

/**
 * Transcript: every turn, always open, paged as it scrolls, with the overlaps and capture
 * gaps on the ladder. Search runs over every turn of the meeting, not only the loaded
 * page; the speaker filter shows one person's turns. While either is on, the list is the
 * matching turns alone, without the overlap and capture-gap rungs between them.
 */
export function TranscriptTab({
  result,
  speakerOrder,
  jump,
  loadNotes,
}: {
  result: SessionResultOut;
  speakerOrder: string[];
  jump: JumpRequest | null;
  /** The meeting's notes in a style, for Export (#774). */
  loadNotes?: () => Promise<MeetingNotesOut | null>;
}) {
  const [query, setQuery] = useState("");
  const [speakerChoice, setSpeakerChoice] = useState<string | null>(null);
  const [entryCount, setEntryCount] = useState(TRANSCRIPT_PAGE_SIZE);
  const [focusStart, setFocusStart] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadLocked = useRef(false);
  const scrollTo = useRef<number | null>(null);

  const segments = result.segments;
  const speakers = useMemo(() => transcriptSpeakers(segments, speakerOrder), [segments, speakerOrder]);
  // A rename swaps a speaker's name under a filter that holds the old one: drop it, so the
  // list does not read as empty.
  const speaker = speakerChoice !== null && speakers.includes(speakerChoice) ? speakerChoice : null;
  const filtering = query.trim() !== "" || speaker !== null;

  const allEntries = useMemo<TimelineEntry[]>(
    () => timelineEntries(segments, result.overlaps ?? [], result.capture_gaps ?? []),
    [segments, result.overlaps, result.capture_gaps],
  );
  const filtered = useMemo(() => filterSegments(segments, query, speaker), [segments, query, speaker]);
  const entries = useMemo<TimelineEntry[]>(
    () => (filtering ? timelineEntries(filtered.segments) : allEntries),
    [filtering, filtered.segments, allEntries],
  );

  // Reset by meeting only: the store re-sends the result while the summary is written and
  // after a speaker rename, and the reader's place must survive both.
  useEffect(() => {
    setQuery("");
    setSpeakerChoice(null);
    setEntryCount(TRANSCRIPT_PAGE_SIZE);
    loadLocked.current = false;
  }, [result.session_id]);

  /** Load one page whenever the end marker becomes visible. */
  useEffect(() => {
    if (entryCount >= entries.length || typeof IntersectionObserver === "undefined") return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    let active = true;
    const observer = new IntersectionObserver(
      (seen) => {
        if (!active || loadLocked.current || !seen.some((entry) => entry.isIntersecting)) return;
        loadLocked.current = true;
        setEntryCount((count) => Math.min(count + TRANSCRIPT_PAGE_SIZE, entries.length));
      },
      { rootMargin: "0px 0px 96px 0px" },
    );
    observer.observe(sentinel);
    return () => {
      active = false;
      observer.disconnect();
      loadLocked.current = false;
    };
  }, [entryCount, entries.length]);

  /*
   * A jump from Quick or Detailed: clear the search and the filter, load as far as the
   * turn covering that moment, then scroll to it and tint it for a moment.
   */
  useEffect(() => {
    if (!jump) return;
    const turn = turnAt(segments, jump.time);
    if (!turn) return;
    setQuery("");
    setSpeakerChoice(null);
    const index = allEntries.findIndex((entry) => entry.kind === "segment" && entry.data === turn);
    setEntryCount((count) => Math.max(count, index + 1));
    setFocusStart(turn.start);
    scrollTo.current = turn.start;
    const timer = setTimeout(() => setFocusStart(null), FOCUS_MS);
    return () => {
      clearTimeout(timer);
      // A cancelled jump takes its tint with it, so the tint can never outlive its timer.
      setFocusStart(null);
    };
    // Only a new jump moves the reader; a re-sent result must not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jump?.nonce]);

  useEffect(() => {
    if (scrollTo.current === null) return;
    const row = listRef.current?.querySelector(`[data-turn-start="${scrollTo.current}"]`);
    if (!row) return;
    scrollTo.current = null;
    const reduced = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo?.({ top: jumpScrollTop(row), behavior: reduced ? "auto" : "smooth" });
  });

  const highlight = useCallback((text: string) => highlightParts(text, query), [query]);

  function changeQuery(next: string): void {
    setQuery(next);
    setEntryCount(TRANSCRIPT_PAGE_SIZE);
  }

  function chooseSpeaker(next: string | null): void {
    setSpeakerChoice(next);
    setEntryCount(TRANSCRIPT_PAGE_SIZE);
  }

  const searching = query.trim() !== "";
  // Only a search can leave nothing: the filter offers only people who spoke.
  const empty = searching && entries.length === 0;

  return (
    // The panel is named by its tab (aria-labelledby on the tabpanel), so no heading. The
    // search and the chips sit on the page's own ground; only the timeline is in a card.
    // A 4-unit step between them: the chip row's own 12-unit touch padding makes up the
    // rest, so search → chips and chips → card each read as the 16-unit section gap.
    <div data-transcript className="flex flex-col gap-1">
        <div role="search" className="flex flex-col gap-tight">
          <div className="relative">
            <SearchIcon
              className="absolute left-input-x top-1/2 -translate-y-1/2 size-icon-xs text-ink-soft pointer-events-none"
            />
            <label className="block">
              <span className="sr-only">{t("result.search_label")}</span>
              <input
                type="search"
                value={query}
                onChange={(e) => changeQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape" && query) changeQuery("");
                }}
                placeholder={t("result.search_placeholder")}
                className="w-full py-2 pl-10 pr-icon-button rounded-control border-0 bg-surface-2 text-ink text-note min-h-icon-button placeholder:text-ink-soft placeholder:opacity-80 appearance-none"
              />
            </label>
            {query ? (
              <button
                type="button"
                aria-label={t("result.clear_search")}
                onClick={() => changeQuery("")}
                className="absolute right-0 top-0 inline-flex size-icon-button items-center justify-center bg-transparent border-none cursor-pointer text-ink-soft hover:text-accent"
              >
                <CloseIcon className="size-icon-xs" />
              </button>
            ) : null}
          </div>
          {searching && !empty ? (
            <span role="status" className={cx(LABEL, "text-count text-ink-soft")}>
              {t("result.search_matches", { count: filtered.matches })}
            </span>
          ) : null}
        </div>

        {/* One row: the speaker chips wrapping on the left, Export at its bottom-right. */}
        <div data-transcript-tools className="flex items-end gap-2">
        {speakers.length > 1 ? (
          <div
            role="group"
            aria-label={t("result.speakers_aria")}
            // Rows 12 apart with 32-unit touch boxes that do not overlap (HIT_CHIP).
            className="flex-1 min-w-0 flex flex-wrap gap-x-tight gap-y-3 py-3"
          >
            {[null, ...speakers].map((name) => {
              const on = name === speaker;
              return (
                <button
                  key={name ?? ""}
                  type="button"
                  aria-pressed={on}
                  onClick={() => chooseSpeaker(name)}
                  className={cx(
                    CHIP_BUTTON,
                    HIT_CHIP,
                    on
                      ? "bg-accent text-accent-ink border-accent"
                      : "bg-surface text-ink border-line hover:border-accent",
                  )}
                >
                  {name === null ? t("result.all_speakers") : speakerName(name)}
                </button>
              );
            })}
          </div>
        ) : (
          <div className="flex-1" />
        )}
          {/* Compact beside the chips; the invisible 44-unit box takes the tap. On a wrap
              it stays at the bottom-right, level with the last row of chips. */}
          <button
            type="button"
            className={cx(
              HIT_44,
              "flex-none self-end mb-3 inline-flex items-center gap-nudge bg-surface border border-line-strong rounded-control cursor-pointer px-2 py-1 font-code text-label font-semibold tracking-label text-ink hover:border-accent hover:text-accent transition-colors",
            )}
            onClick={() => {
              captureAnalytics("result_exported", {});
              // The notes written in a style go in too (#774), whether or not Detailed was
              // opened; if they cannot be read, the file is the summary and transcript.
              if (!loadNotes) return exportTranscript(result);
              void loadNotes().then(
                (notes) => exportTranscript(result, notes),
                () => exportTranscript(result),
              );
            }}
          >
            <DownloadIcon className="size-icon-xs" />
            <span>{t("result.export")}</span>
          </button>
        </div>

      <section data-transcript-card className="bg-surface border border-line rounded-card p-4">
        {empty ? (
          <p role="status" className="m-0 text-note text-ink-soft">
            {t("result.search_empty", { query: query.trim() })}
          </p>
        ) : (
          <div
            id="result-transcript-timeline"
            ref={listRef}
            aria-label={t("result.transcript_entries_aria")}
          >
            <Timeline
              entries={entries.slice(0, entryCount)}
              overlaps={filtering ? [] : (result.overlaps ?? [])}
              speakerOrder={speakerOrder}
              highlight={searching ? highlight : undefined}
              focusStart={focusStart}
            />
            {entryCount < entries.length ? (
              <div ref={sentinelRef} className="h-px" aria-hidden="true" />
            ) : null}
          </div>
        )}
      </section>
    </div>
  );
}
